import React, { useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ExitToApp } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { ReactComponent as SAVILogo } from 'assets/savi_icon.svg';
import Button from 'components/Button';
import CreateDeviceModal from 'components/CreateModal/CreateDeviceModal';
import CreateSetModal from 'components/CreateModal/CreateSetModal';
import RestrictedComponent from 'components/RestrictedComponent/RestrictedComponent';
import { useLanguage, useSearch } from 'context/AppContext';
import { useUser } from 'store/user/useUser';
import { isSatoVideoReviewer } from 'utils/helpers/permissions.helpers';
import { navigationLinks } from './Navigation.constants';
import {
  LogoContainer,
  NavButtonContainer,
  NavigationContainer,
  NavLinksContainer,
  NavListItem,
  NavListLogout,
} from './Navigation.styles';

const checkActive = (locationPath: string, linkPath: string): boolean => {
  return locationPath.split('/')[1] === linkPath.split('/')[1];
};

export const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [language] = useLanguage();
  const user = useUser();
  const [, dispatchSearch] = useSearch();
  const [openSetModal, setOpenSetModal] = useState(false);
  const [openDeviceModal, setOpenDeviceModal] = useState(false);
  const { logout: auth0Logout } = useAuth0();

  const handleNavigation = (route: string): void => {
    navigate(route);
    dispatchSearch('');
  };

  const handleLogout = async (): Promise<void> => {
    auth0Logout({ returnTo: window.location.origin });
  };

  const canCreateDevicesAndSets = useMemo(() => !isSatoVideoReviewer(user), [user]);

  const handleClickLogo = () => {
    navigate('');
  };

  return (
    <NavigationContainer>
      <LogoContainer onClick={handleClickLogo}>
        {user.isAuthenticated && <SAVILogo title="Logo" style={{ maxHeight: '100%' }} />}
      </LogoContainer>
      {canCreateDevicesAndSets ? (
        <NavButtonContainer>
          <Button sx={{ mb: 1 }} onClick={() => setOpenSetModal(true)} fullWidth>
            {language.Navigation.createSet}
          </Button>
          <Button onClick={() => setOpenDeviceModal(true)} fullWidth>
            {language.Navigation.createDevice}
          </Button>
        </NavButtonContainer>
      ) : null}
      <NavLinksContainer>
        {navigationLinks.map(navLink => {
          const { icon, name, route, roles = [], featureFlag } = navLink;
          return (
            <RestrictedComponent key={route} featureFlag={featureFlag} allowedRoles={roles}>
              <NavListItem
                key={route}
                onClick={() => handleNavigation(route)}
                active={checkActive(location.pathname, route)}
              >
                {icon}
                {language.Routes[name]}
              </NavListItem>
            </RestrictedComponent>
          );
        })}
        <NavListLogout key={'logout'} onClick={handleLogout} active={false}>
          <ExitToApp sx={{ mr: '1rem' }} />
          {language.Navigation.logout}
        </NavListLogout>
      </NavLinksContainer>
      <CreateSetModal open={openSetModal} close={() => setOpenSetModal(false)} />
      <CreateDeviceModal open={openDeviceModal} close={() => setOpenDeviceModal(false)} />
    </NavigationContainer>
  );
};
